<template>
  <b-modal
    id="vulnerabilityDetailsModal"
    size="xl"
    @show="onShow()"
    hide-header-close
    :title="$t('message.vulnerability_details')"
  >
    <b-tabs class="body-bg-color" style="border: 0; padding: 0">
      <b-tab class="body-bg-color" style="border: 0; padding: 0" active>
        <template v-slot:title
          ><i class="fa fa-edit"></i> {{ $t('message.general') }}</template
        >
        <b-card>
          <b-input-group-form-input
            id="vulnerability-id-input"
            input-group-size="mb-3"
            type="text"
            v-model="vulnerability.vulnId"
            lazy="false"
            required="false"
            feedback="false"
            autofocus="false"
            disabled="true"
            :label="$t('message.vulnerability_vuln_id')"
            :tooltip="this.$t('message.vulnerability_vuln_id_desc')"
            :feedback-text="$t('message.required_vulnerability_vuln_id')"
          />
          <b-input-group-form-input
            id="vulnerability-title-input"
            input-group-size="mb-3"
            type="text"
            v-model="vulnerability.title"
            lazy="true"
            required="false"
            feedback="false"
            autofocus="false"
            :label="$t('message.vulnerability_title')"
            :tooltip="this.$t('message.vulnerability_title_desc')"
            :readonly="isReadonly"
          />
          <b-row>
            <b-col md="4">
              <b-input-group-form-select
                required="true"
                v-model="vulnerability.severity"
                :options="severities"
                :label="$t('message.severity')"
                :tooltip="$t('message.severity')"
                :disabled="isReadonly"
              />
            </b-col>
            <b-col md="4">
              <b-input-group-form-input
                input-group-size="mb-3"
                type="text"
                v-model="computedCvssSeverity"
                lazy="true"
                required="false"
                feedback="false"
                autofocus="false"
                disabled="true"
                :label="$t('severity.cvss_severity')"
                :tooltip="this.$t('message.severity')"
              />
            </b-col>
            <b-col md="4">
              <b-input-group-form-input
                input-group-size="mb-3"
                type="text"
                v-model="computedOwaspRRSeverity"
                lazy="true"
                required="false"
                feedback="false"
                autofocus="false"
                disabled="true"
                :label="$t('severity.owasp_rr_severity')"
                :tooltip="this.$t('message.severity')"
              />
            </b-col>
          </b-row>
          <b-form-group :label="this.$t('message.aliases')">
            <div class="list-group">
              <span v-for="alias in resolveVulnAliases(vulnerability.aliases)">
                <actionable-list-group-item
                  :value="alias.vulnId"
                  :delete-icon="false"
                />
              </span>
              <actionable-list-group-item
                :add-icon="!isReadonly"
                v-on:actionClicked="
                  $root.$emit('bv::show::modal', 'selectCweModal')
                "
              />
            </div>
          </b-form-group>
          <b-form-group :label="this.$t('message.cwe')">
            <div class="list-group">
              <span v-for="cwe in selectableCwes">
                <actionable-list-group-item
                  :value="'CWE-' + cwe.cweId"
                  :delete-icon="!isReadonly"
                  v-on:actionClicked="removeCwe(cwe)"
                />
              </span>
              <actionable-list-group-item
                :add-icon="!isReadonly"
                v-on:actionClicked="
                  $root.$emit('bv::show::modal', 'selectCweModal')
                "
              />
            </div>
          </b-form-group>
          <b-input-group-form-input
            id="vulnerability-uuid"
            input-group-size="mb-3"
            type="text"
            v-model="vulnerability.uuid"
            lazy="false"
            required="false"
            feedback="false"
            autofocus="false"
            disabled="true"
            :label="$t('message.object_identifier')"
            :tooltip="this.$t('message.object_identifier_desc')"
            :readonly="true"
          />
        </b-card>
      </b-tab>
      <b-tab class="body-bg-color" style="border: 0; padding: 0">
        <template v-slot:title
          ><i class="fa fa-newspaper-o"></i>
          {{ $t('message.extended') }}</template
        >
        <b-card>
          <b-form-group
            id="vulnerability-description-form-group"
            :label="this.$t('message.description')"
            label-for="vulnerability-description-input"
          >
            <b-form-textarea
              id="vulnerability-description-description"
              v-model="vulnerability.description"
              rows="5"
              :readonly="isReadonly"
            />
          </b-form-group>
          <b-form-group
            id="vulnerability-detail-form-group"
            :label="this.$t('message.details')"
            label-for="vulnerability-detail-input"
          >
            <b-form-textarea
              id="vulnerability-detail-description"
              v-model="vulnerability.detail"
              rows="5"
              :readonly="isReadonly"
            />
          </b-form-group>
          <b-form-group
            id="vulnerability-recommendation-form-group"
            :label="this.$t('message.recommendation')"
            label-for="vulnerability-recommendation-input"
          >
            <b-form-textarea
              id="vulnerability-recommendation-description"
              v-model="vulnerability.recommendation"
              rows="5"
              :readonly="isReadonly"
            />
          </b-form-group>
          <b-form-group
            id="vulnerability-references-form-group"
            :label="this.$t('message.references')"
            label-for="vulnerability-references-input"
          >
            <b-form-textarea
              id="vulnerability-references-description"
              v-model="vulnerability.references"
              rows="5"
              :readonly="isReadonly"
            />
          </b-form-group>
        </b-card>
      </b-tab>
      <b-tab class="body-bg-color" style="border: 0; padding: 0">
        <template v-slot:title
          ><i class="fa fa-calculator"></i>
          {{ $t('message.cvss_v2') }}</template
        >
        <b-card>
          <b-row>
            <b-col sm="4" class="text-center">
              <vue-easy-pie-chart
                :bar-color="severityColor(cvssV2Score.baseScore)"
                :track-color="recessedColor"
                :scale-color="primaryColor"
                line-cap="butt"
                :line-width="5"
                :scale-length="5"
                :percent="cvssV2Score.baseScore * 10"
                :size="90"
                :animate="true"
                >{{ cvssV2Score.baseScore.toFixed(1) }}</vue-easy-pie-chart
              >
              <h6>{{ $t('message.cvss_base_score_short') }}</h6>
            </b-col>
            <b-col sm="4" class="text-center">
              <vue-easy-pie-chart
                :bar-color="primaryColor"
                :track-color="recessedColor"
                :scale-color="primaryColor"
                line-cap="butt"
                :line-width="5"
                :scale-length="5"
                :percent="cvssV2Score.impactSubScore * 10"
                :size="90"
                :animate="true"
                >{{ cvssV2Score.impactSubScore.toFixed(1) }}</vue-easy-pie-chart
              >
              <h6>{{ $t('message.cvss_impact') }}</h6>
            </b-col>
            <b-col sm="4" class="text-center">
              <vue-easy-pie-chart
                :bar-color="primaryColor"
                :track-color="recessedColor"
                :scale-color="primaryColor"
                line-cap="butt"
                :line-width="5"
                :scale-length="5"
                :percent="cvssV2Score.exploitabilitySubScore * 10"
                :size="90"
                :animate="true"
                >{{
                  cvssV2Score.exploitabilitySubScore.toFixed(1)
                }}</vue-easy-pie-chart
              >
              <h6>{{ $t('message.cvss_exploitability') }}</h6>
            </b-col>
          </b-row>
          <b-row style="margin-top: 2rem">
            <b-col sm="6">
              <b-form-group
                :label="$t('message.cvss_attack_vector')"
                v-slot="{ cvssv2AttackVector }"
              >
                <b-form-radio-group
                  v-model="cvssv2Vector.av"
                  :options="cvssv2Options.av"
                  :aria-describedby="cvssv2AttackVector"
                  name="radios-btn-default"
                  v-on:change="generateCvssV2Vector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-3-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.cvss_access_complexity')"
                v-slot="{ cvssv2AccessComplexity }"
              >
                <b-form-radio-group
                  v-model="cvssv2Vector.ac"
                  :options="cvssv2Options.ac"
                  :aria-describedby="cvssv2AccessComplexity"
                  name="radios-btn-default"
                  v-on:change="generateCvssV2Vector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-3-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.cvss_authentication')"
                v-slot="{ cvssv2Authentication }"
              >
                <b-form-radio-group
                  v-model="cvssv2Vector.au"
                  :options="cvssv2Options.au"
                  :aria-describedby="cvssv2Authentication"
                  name="radios-btn-default"
                  v-on:change="generateCvssV2Vector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-3-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group
                :label="$t('message.cvss_confidentiality_impact')"
                v-slot="{ cvssv2ConfidentialityImpact }"
              >
                <b-form-radio-group
                  v-model="cvssv2Vector.c"
                  :options="cvssv2Options.c"
                  :aria-describedby="cvssv2ConfidentialityImpact"
                  name="radios-btn-default"
                  v-on:change="generateCvssV2Vector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-3-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.cvss_integrity_impact')"
                v-slot="{ cvssv2IntegrityImpact }"
              >
                <b-form-radio-group
                  v-model="cvssv2Vector.i"
                  :options="cvssv2Options.i"
                  :aria-describedby="cvssv2IntegrityImpact"
                  name="radios-btn-default"
                  v-on:change="generateCvssV2Vector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-3-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.cvss_availability_impact')"
                v-slot="{ cvssv2AvailabilityImpact }"
              >
                <b-form-radio-group
                  v-model="cvssv2Vector.a"
                  :options="cvssv2Options.a"
                  :aria-describedby="cvssv2AvailabilityImpact"
                  name="radios-btn-default"
                  v-on:change="generateCvssV2Vector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-3-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
      </b-tab>
      <b-tab class="body-bg-color" style="border: 0; padding: 0">
        <template v-slot:title
          ><i class="fa fa-calculator"></i>
          {{ $t('message.cvss_v3') }}</template
        >
        <b-card>
          <b-row>
            <b-col sm="4" class="text-center">
              <vue-easy-pie-chart
                :bar-color="severityColor(cvssV3Score.baseScore)"
                :track-color="recessedColor"
                :scale-color="primaryColor"
                line-cap="butt"
                :line-width="5"
                :scale-length="5"
                :percent="cvssV3Score.baseScore * 10"
                :size="90"
                :animate="true"
                >{{ cvssV3Score.baseScore.toFixed(1) }}</vue-easy-pie-chart
              >
              <h6>{{ $t('message.cvss_base_score_short') }}</h6>
            </b-col>
            <b-col sm="4" class="text-center">
              <vue-easy-pie-chart
                :bar-color="primaryColor"
                :track-color="recessedColor"
                :scale-color="primaryColor"
                line-cap="butt"
                :line-width="5"
                :scale-length="5"
                :percent="cvssV3Score.impactSubScore * 10"
                :size="90"
                :animate="true"
                >{{ cvssV3Score.impactSubScore.toFixed(1) }}</vue-easy-pie-chart
              >
              <h6>{{ $t('message.cvss_impact') }}</h6>
            </b-col>
            <b-col sm="4" class="text-center">
              <vue-easy-pie-chart
                :bar-color="primaryColor"
                :track-color="recessedColor"
                :scale-color="primaryColor"
                line-cap="butt"
                :line-width="5"
                :scale-length="5"
                :percent="cvssV3Score.exploitabilitySubScore * 10"
                :size="90"
                :animate="true"
                >{{
                  cvssV3Score.exploitabilitySubScore.toFixed(1)
                }}</vue-easy-pie-chart
              >
              <h6>{{ $t('message.cvss_exploitability') }}</h6>
            </b-col>
          </b-row>
          <b-row style="margin-top: 2rem">
            <b-col sm="6">
              <b-form-group
                :label="$t('message.cvss_attack_vector')"
                v-slot="{ cvssv3AttackVector }"
              >
                <b-form-radio-group
                  v-model="cvssv3Vector.av"
                  :options="cvssv3Options.av"
                  :aria-describedby="cvssv3AttackVector"
                  name="radios-btn-default"
                  v-on:change="generateCvssV3Vector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-4-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.cvss_attack_complexity')"
                v-slot="{ cvssv3AttackComplexity }"
              >
                <b-form-radio-group
                  v-model="cvssv3Vector.ac"
                  :options="cvssv3Options.ac"
                  :aria-describedby="cvssv3AttackComplexity"
                  name="radios-btn-default"
                  v-on:change="generateCvssV3Vector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-2-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.cvss_privileges_required')"
                v-slot="{ cvssv3PrivilegesRequired }"
              >
                <b-form-radio-group
                  v-model="cvssv3Vector.pr"
                  :options="cvssv3Options.pr"
                  :aria-describedby="cvssv3PrivilegesRequired"
                  name="radios-btn-default"
                  v-on:change="generateCvssV3Vector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-3-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.cvss_user_interaction')"
                v-slot="{ cvssv3UserInteraction }"
              >
                <b-form-radio-group
                  v-model="cvssv3Vector.ui"
                  :options="cvssv3Options.ui"
                  :aria-describedby="cvssv3UserInteraction"
                  name="radios-btn-default"
                  v-on:change="generateCvssV3Vector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-2-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group
                :label="$t('message.cvss_scope')"
                v-slot="{ cvssv3Scope }"
              >
                <b-form-radio-group
                  v-model="cvssv3Vector.s"
                  :options="cvssv3Options.s"
                  :aria-describedby="cvssv3Scope"
                  name="radios-btn-default"
                  v-on:change="generateCvssV3Vector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-2-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.cvss_confidentiality_impact')"
                v-slot="{ cvssv3ConfidentialityImpact }"
              >
                <b-form-radio-group
                  v-model="cvssv3Vector.c"
                  :options="cvssv3Options.c"
                  :aria-describedby="cvssv3ConfidentialityImpact"
                  name="radios-btn-default"
                  v-on:change="generateCvssV3Vector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-3-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.cvss_integrity_impact')"
                v-slot="{ cvssv3IntegrityImpact }"
              >
                <b-form-radio-group
                  v-model="cvssv3Vector.i"
                  :options="cvssv3Options.i"
                  :aria-describedby="cvssv3IntegrityImpact"
                  name="radios-btn-default"
                  v-on:change="generateCvssV3Vector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-3-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.cvss_availability_impact')"
                v-slot="{ cvssv3AvailabilityImpact }"
              >
                <b-form-radio-group
                  v-model="cvssv3Vector.a"
                  :options="cvssv3Options.a"
                  :aria-describedby="cvssv3AvailabilityImpact"
                  name="radios-btn-default"
                  v-on:change="generateCvssV3Vector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-3-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
      </b-tab>
      <b-tab class="body-bg-color" style="border: 0; padding: 0">
        <template v-slot:title
          ><i class="fa fa-calculator"></i>
          {{ $t('message.owasp_rr') }}</template
        >
        <b-card>
          <b-row>
            <b-col sm="4" class="text-center">
              <vue-easy-pie-chart
                :bar-color="owaspRRSeverityColor(owaspRRScore.likelihoodScore)"
                :track-color="recessedColor"
                :scale-color="primaryColor"
                line-cap="butt"
                :line-width="5"
                :scale-length="5"
                :percent="owaspRRScore.likelihoodScore * 10"
                :size="90"
                :animate="true"
                >{{
                  owaspRRScore.likelihoodScore.toFixed(1)
                }}</vue-easy-pie-chart
              >
              <h6>{{ $t('message.owasp_rr_likelihood_score_short') }}</h6>
            </b-col>
            <b-col sm="4" class="text-center">
              <vue-easy-pie-chart
                :bar-color="
                  owaspRRSeverityColor(owaspRRScore.technicalImpactScore)
                "
                :track-color="recessedColor"
                :scale-color="primaryColor"
                line-cap="butt"
                :line-width="5"
                :scale-length="5"
                :percent="owaspRRScore.technicalImpactScore * 10"
                :size="90"
                :animate="true"
                >{{
                  owaspRRScore.technicalImpactScore.toFixed(1)
                }}</vue-easy-pie-chart
              >
              <h6>{{ $t('message.owasp_rr_technical_impact_score_short') }}</h6>
            </b-col>
            <b-col sm="4" class="text-center">
              <vue-easy-pie-chart
                :bar-color="
                  owaspRRSeverityColor(owaspRRScore.businessImpactScore)
                "
                :track-color="recessedColor"
                :scale-color="primaryColor"
                line-cap="butt"
                :line-width="5"
                :scale-length="5"
                :percent="owaspRRScore.businessImpactScore * 10"
                :size="90"
                :animate="true"
                >{{
                  owaspRRScore.businessImpactScore.toFixed(1)
                }}</vue-easy-pie-chart
              >
              <h6>{{ $t('message.owasp_rr_business_impact_score_short') }}</h6>
            </b-col>
          </b-row>
          <b-row style="margin-top: 2rem">
            <b-col sm="6">
              <h6>{{ $t('message.owasp_rr_threat_agent_factor') }}</h6>
              <b-form-group
                :label="$t('message.owasp_rr_skill_level')"
                v-slot="{ owaspRRSkillLevel }"
              >
                <b-form-radio-group
                  v-model="owaspRRVector.sl"
                  :options="owaspRROptions.sl"
                  :aria-describedby="owaspRRSkillLevel"
                  name="radios-btn-default"
                  v-on:change="generateOwaspRRVector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-4-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.owasp_rr_motivation')"
                v-slot="{ owaspRRMotivation }"
              >
                <b-form-radio-group
                  v-model="owaspRRVector.m"
                  :options="owaspRROptions.m"
                  :aria-describedby="owaspRRMotivation"
                  name="radios-btn-default"
                  v-on:change="generateOwaspRRVector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-4-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.owasp_rr_opportunity')"
                v-slot="{ owaspRROpportunity }"
              >
                <b-form-radio-group
                  v-model="owaspRRVector.o"
                  :options="owaspRROptions.o"
                  :aria-describedby="owaspRROpportunity"
                  name="radios-btn-default"
                  v-on:change="generateOwaspRRVector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-4-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.owasp_rr_threat_size')"
                v-slot="{ owaspRRThreatSize }"
              >
                <b-form-radio-group
                  v-model="owaspRRVector.s"
                  :options="owaspRROptions.s"
                  :aria-describedby="owaspRRThreatSize"
                  name="radios-btn-default"
                  v-on:change="generateOwaspRRVector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-4-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <h6>{{ $t('message.owasp_rr_vulnerability_factor') }}</h6>
              <b-form-group
                :label="$t('message.owasp_rr_ease_of_discovery')"
                v-slot="{ owaspRREaseOfDiscovery }"
              >
                <b-form-radio-group
                  v-model="owaspRRVector.ed"
                  :options="owaspRROptions.ed"
                  :aria-describedby="owaspRREaseOfDiscovery"
                  name="radios-btn-default"
                  v-on:change="generateOwaspRRVector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-2-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.owasp_rr_ease_of_exploit')"
                v-slot="{ owaspRREaseOfExploit }"
              >
                <b-form-radio-group
                  v-model="owaspRRVector.ee"
                  :options="owaspRROptions.ee"
                  :aria-describedby="owaspRREaseOfExploit"
                  name="radios-btn-default"
                  v-on:change="generateOwaspRRVector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-2-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.owasp_rr_awareness')"
                v-slot="{ owaspRRAwareness }"
              >
                <b-form-radio-group
                  v-model="owaspRRVector.a"
                  :options="owaspRROptions.a"
                  :aria-describedby="owaspRRAwareness"
                  name="radios-btn-default"
                  v-on:change="generateOwaspRRVector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-2-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.owasp_rr_intrusion_detection')"
                v-slot="{ owaspRRIntrusionDetection }"
              >
                <b-form-radio-group
                  v-model="owaspRRVector.id"
                  :options="owaspRROptions.id"
                  :aria-describedby="owaspRRIntrusionDetection"
                  name="radios-btn-default"
                  v-on:change="generateOwaspRRVector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-2-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <h6>{{ $t('message.owasp_rr_technical_impact_factor') }}</h6>
              <b-form-group
                :label="$t('message.owasp_rr_loss_of_confidentiality')"
                v-slot="{ owaspRRLossOfConfidentiality }"
              >
                <b-form-radio-group
                  v-model="owaspRRVector.lc"
                  :options="owaspRROptions.lc"
                  :aria-describedby="owaspRRLossOfConfidentiality"
                  name="radios-btn-default"
                  v-on:change="generateOwaspRRVector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-2-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.owasp_rr_loss_of_integrity')"
                v-slot="{ owaspRRLossOfIntegrity }"
              >
                <b-form-radio-group
                  v-model="owaspRRVector.li"
                  :options="owaspRROptions.li"
                  :aria-describedby="owaspRRLossOfIntegrity"
                  name="radios-btn-default"
                  v-on:change="generateOwaspRRVector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-2-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.owasp_rr_loss_of_availability')"
                v-slot="{ owaspRRLossOfAvailibility }"
              >
                <b-form-radio-group
                  v-model="owaspRRVector.lav"
                  :options="owaspRROptions.lav"
                  :aria-describedby="owaspRRLossOfAvailibility"
                  name="radios-btn-default"
                  v-on:change="generateOwaspRRVector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-2-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.owasp_rr_loss_of_accountability')"
                v-slot="{ owaspRRLossOfAccountability }"
              >
                <b-form-radio-group
                  v-model="owaspRRVector.lac"
                  :options="owaspRROptions.lac"
                  :aria-describedby="owaspRRLossOfAccountability"
                  name="radios-btn-default"
                  v-on:change="generateOwaspRRVector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-2-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <h6>{{ $t('message.owasp_rr_business_impact_factor') }}</h6>
              <b-form-group
                :label="$t('message.owasp_rr_financial_damage')"
                v-slot="{ owaspRRFinancialDamage }"
              >
                <b-form-radio-group
                  v-model="owaspRRVector.fd"
                  :options="owaspRROptions.fd"
                  :aria-describedby="owaspRRFinancialDamage"
                  name="radios-btn-default"
                  v-on:change="generateOwaspRRVector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-2-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.owasp_rr_reputation_damage')"
                v-slot="{ owaspRRReputationDamage }"
              >
                <b-form-radio-group
                  v-model="owaspRRVector.rd"
                  :options="owaspRROptions.rd"
                  :aria-describedby="owaspRRReputationDamage"
                  name="radios-btn-default"
                  v-on:change="generateOwaspRRVector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-2-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.owasp_rr_non_compliance')"
                v-slot="{ owaspRRNonCompliance }"
              >
                <b-form-radio-group
                  v-model="owaspRRVector.nc"
                  :options="owaspRROptions.nc"
                  :aria-describedby="owaspRRNonCompliance"
                  name="radios-btn-default"
                  v-on:change="generateOwaspRRVector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-2-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
              <b-form-group
                :label="$t('message.owasp_rr_privacy_violation')"
                v-slot="{ owaspRRPrivacyViolation }"
              >
                <b-form-radio-group
                  v-model="owaspRRVector.pv"
                  :options="owaspRROptions.pv"
                  :aria-describedby="owaspRRPrivacyViolation"
                  name="radios-btn-default"
                  v-on:change="generateOwaspRRVector"
                  button-variant="outline-primary"
                  class="cvss-calc cvss-calc-2-btn"
                  buttons
                  :disabled="isReadonly"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
      </b-tab>
      <b-tab class="body-bg-color" style="border: 0; padding: 0">
        <template v-slot:title
          ><i class="fa fa-cubes"></i>
          {{ $t('message.affected_components') }}</template
        >
        <b-card>
          <bootstrap-table
            ref="table"
            :columns="columns"
            :data="this.affectedVersionAttributions"
            :options="options"
          >
          </bootstrap-table>
        </b-card>
      </b-tab>
      <b-tab class="body-bg-color" style="border: 0; padding: 0">
        <template v-slot:title
          ><i class="fa fa-calendar"></i> {{ $t('message.dates') }}</template
        >
        <b-card>
          <b-input-group-form-datepicker
            id="vulnerability-created-input"
            input-group-size="mb-3"
            v-model="vulnerability.created"
            lazy="true"
            required="false"
            feedback="false"
            autofocus="false"
            placeholder="YYYY-MM-DD"
            :label="$t('message.created')"
            :tooltip="this.$t('message.vulnerability_created_desc')"
          />
          <b-input-group-form-datepicker
            id="vulnerability-published_input"
            input-group-size="mb-3"
            v-model="vulnerability.published"
            lazy="true"
            required="false"
            feedback="false"
            autofocus="false"
            placeholder="YYYY-MM-DD"
            :label="$t('message.published')"
            :tooltip="this.$t('message.vulnerability_published_desc')"
            :disabled="isReadonly"
          />
          <b-input-group-form-datepicker
            id="vulnerability-updated-input"
            input-group-size="mb-3"
            v-model="vulnerability.updated"
            lazy="true"
            required="false"
            feedback="false"
            autofocus="false"
            placeholder="YYYY-MM-DD"
            :label="$t('message.updated')"
            :tooltip="this.$t('message.vulnerability_updated_desc')"
            :readonly="isReadonly"
          />
        </b-card>
      </b-tab>
    </b-tabs>
    <template v-slot:modal-footer="{ cancel }">
      <b-button
        size="md"
        variant="outline-danger"
        @click="deleteVulnerability()"
        :disabled="
          vulnerability.components && vulnerability.components.length > 0
        "
        v-show="!isReadonly"
        v-permission="PERMISSIONS.VULNERABILITY_MANAGEMENT"
        >{{ $t('message.delete') }}</b-button
      >
      <b-button size="md" variant="secondary" @click="cancel()">{{
        $t('message.close')
      }}</b-button>
      <b-button
        size="md"
        variant="primary"
        @click="updateVulnerability()"
        v-show="!isReadonly"
        v-permission="PERMISSIONS.VULNERABILITY_MANAGEMENT"
        >{{ $t('message.update') }}</b-button
      >
    </template>
    <select-cwe-modal v-on:selection="updateCweSelection" />
    <add-affected-component-modal
      v-on:affectedComponentAdded="addAffectedComponent"
    />
  </b-modal>
</template>

<script>
import BInputGroupFormDatepicker from '../../../forms/BInputGroupFormDatepicker';
import BInputGroupFormInput from '../../../forms/BInputGroupFormInput';
import BInputGroupFormSelect from '../../../forms/BInputGroupFormSelect';
import VueTagsInput from '@johmun/vue-tags-input';
import { Switch as cSwitch } from '@coreui/vue';
import permissionsMixin from '../../../mixins/permissionsMixin';
import ActionableListGroupItem from '../../components/ActionableListGroupItem';
import SelectCweModal from './SelectCweModal';
import AddAffectedComponentModal from './AddAffectedComponentModal';
import VueEasyPieChart from 'vue-easy-pie-chart';
import { getStyle } from '@coreui/coreui/dist/js/coreui-utilities';
import { isEqual } from 'lodash-es';
import common from '../../../shared/common';

export default {
  name: 'VulnerabilityDetailsVulnerabilityModal',
  mixins: [permissionsMixin],
  components: {
    ActionableListGroupItem,
    BInputGroupFormDatepicker,
    BInputGroupFormInput,
    BInputGroupFormSelect,
    SelectCweModal,
    AddAffectedComponentModal,
    VueEasyPieChart,
    VueTagsInput,
    cSwitch,
  },
  props: {
    vulnProp: Object,
  },
  data() {
    return {
      newAdded: 0,
      canChange: null,
      primaryColor: getStyle('--primary'),
      recessedColor: getStyle('--recessed'),
      criticalColor: getStyle('--severity-critical'),
      highColor: getStyle('--severity-high'),
      mediumColor: getStyle('--severity-medium'),
      lowColor: getStyle('--severity-low'),
      selectableCwes: [],
      selectableAliases: [],
      affectedVersionAttributions: [],
      vulnerability: {
        vulnId: null, // This has to be null on initial load so that the vulnId gets populated.
        source: 'INTERNAL',
        affectedComponents: [],
      },
      severities: [
        {
          value: null,
          text: this.$i18n.t('severity.derive_from_cvss_or_owasp_rr'),
        },
        { value: 'CRITICAL', text: this.$i18n.t('severity.critical') },
        { value: 'HIGH', text: this.$i18n.t('severity.high') },
        { value: 'MEDIUM', text: this.$i18n.t('severity.medium') },
        { value: 'LOW', text: this.$i18n.t('severity.low') },
        { value: 'INFO', text: this.$i18n.t('severity.info') },
        { value: 'UNASSIGNED', text: this.$i18n.t('severity.unassigned') },
      ],
      cvssv2Vector: {
        av: null,
        ac: null,
        au: null,
        c: null,
        i: null,
        a: null,
      },
      cvssv3Vector: {
        av: null,
        ac: null,
        pr: null,
        ui: null,
        s: null,
        c: null,
        i: null,
        a: null,
      },
      owaspRRVector: {
        sl: null,
        m: null,
        o: null,
        s: null,
        ed: null,
        ee: null,
        a: null,
        id: null,
        lc: null,
        li: null,
        lav: null,
        lac: null,
        fd: null,
        rd: null,
        nc: null,
        pv: null,
      },
      cvssV2Score: {
        baseScore: 0,
        impactSubScore: 0,
        exploitabilitySubScore: 0,
      },
      cvssV3Score: {
        baseScore: 0,
        impactSubScore: 0,
        exploitabilitySubScore: 0,
      },
      owaspRRScore: {
        likelihoodScore: 0,
        technicalImpactScore: 0,
        businessImpactScore: 0,
      },
      cvssv2Options: {
        av: [
          { text: this.$i18n.t('message.cvss_local'), value: 'L' },
          { text: this.$i18n.t('message.cvss_adjacent'), value: 'A' },
          { text: this.$i18n.t('message.cvss_network'), value: 'N' },
        ],
        ac: [
          { text: this.$i18n.t('message.cvss_high'), value: 'H' },
          { text: this.$i18n.t('message.cvss_medium'), value: 'M' },
          { text: this.$i18n.t('message.cvss_low'), value: 'L' },
        ],
        au: [
          { text: this.$i18n.t('message.cvss_multiple'), value: 'M' },
          { text: this.$i18n.t('message.cvss_single'), value: 'S' },
          { text: this.$i18n.t('message.cvss_none'), value: 'N' },
        ],
        c: [
          { text: this.$i18n.t('message.cvss_none'), value: 'N' },
          { text: this.$i18n.t('message.cvss_partial'), value: 'P' },
          { text: this.$i18n.t('message.cvss_complete'), value: 'C' },
        ],
        i: [
          { text: this.$i18n.t('message.cvss_none'), value: 'N' },
          { text: this.$i18n.t('message.cvss_partial'), value: 'P' },
          { text: this.$i18n.t('message.cvss_complete'), value: 'C' },
        ],
        a: [
          { text: this.$i18n.t('message.cvss_none'), value: 'N' },
          { text: this.$i18n.t('message.cvss_partial'), value: 'P' },
          { text: this.$i18n.t('message.cvss_complete'), value: 'C' },
        ],
      },
      cvssv3Options: {
        av: [
          { text: this.$i18n.t('message.cvss_physical'), value: 'P' },
          { text: this.$i18n.t('message.cvss_local'), value: 'L' },
          { text: this.$i18n.t('message.cvss_adjacent'), value: 'A' },
          { text: this.$i18n.t('message.cvss_network'), value: 'N' },
        ],
        ac: [
          { text: this.$i18n.t('message.cvss_low'), value: 'L' },
          { text: this.$i18n.t('message.cvss_high'), value: 'H' },
        ],
        pr: [
          { text: this.$i18n.t('message.cvss_none'), value: 'N' },
          { text: this.$i18n.t('message.cvss_low'), value: 'L' },
          { text: this.$i18n.t('message.cvss_high'), value: 'H' },
        ],
        ui: [
          { text: this.$i18n.t('message.cvss_none'), value: 'N' },
          { text: this.$i18n.t('message.cvss_required'), value: 'R' },
        ],
        s: [
          { text: this.$i18n.t('message.cvss_unchanged'), value: 'U' },
          { text: this.$i18n.t('message.cvss_changed'), value: 'C' },
        ],
        c: [
          { text: this.$i18n.t('message.cvss_none'), value: 'N' },
          { text: this.$i18n.t('message.cvss_low'), value: 'L' },
          { text: this.$i18n.t('message.cvss_high'), value: 'H' },
        ],
        i: [
          { text: this.$i18n.t('message.cvss_none'), value: 'N' },
          { text: this.$i18n.t('message.cvss_low'), value: 'L' },
          { text: this.$i18n.t('message.cvss_high'), value: 'H' },
        ],
        a: [
          { text: this.$i18n.t('message.cvss_none'), value: 'N' },
          { text: this.$i18n.t('message.cvss_low'), value: 'L' },
          { text: this.$i18n.t('message.cvss_high'), value: 'H' },
        ],
      },
      owaspRROptions: {
        sl: [
          {
            text: this.$i18n.t('message.owasp_rr_skill_level_none'),
            value: '1',
          },
          {
            text: this.$i18n.t('message.owasp_rr_skill_level_some'),
            value: '3',
          },
          {
            text: this.$i18n.t('message.owasp_rr_skill_level_advanced'),
            value: '5',
          },
          {
            text: this.$i18n.t(
              'message.owasp_rr_skill_level_network_and_programming',
            ),
            value: '6',
          },
          {
            text: this.$i18n.t(
              'message.owasp_rr_skill_level_security_penetration_testing',
            ),
            value: '9',
          },
        ],
        m: [
          { text: this.$i18n.t('message.owasp_rr_motivation_low'), value: '1' },
          {
            text: this.$i18n.t('message.owasp_rr_motivation_possible_reward'),
            value: '4',
          },
          {
            text: this.$i18n.t('message.owasp_rr_motivation_high_reward'),
            value: '9',
          },
        ],
        o: [
          {
            text: this.$i18n.t('message.owasp_rr_opportunity_full'),
            value: '0',
          },
          {
            text: this.$i18n.t('message.owasp_rr_opportunity_special'),
            value: '4',
          },
          {
            text: this.$i18n.t('message.owasp_rr_opportunity_some'),
            value: '7',
          },
          {
            text: this.$i18n.t('message.owasp_rr_opportunity_none'),
            value: '9',
          },
        ],
        s: [
          {
            text: this.$i18n.t('message.owasp_rr_threat_size_dev_sa'),
            value: '2',
          },
          {
            text: this.$i18n.t('message.owasp_rr_threat_size_intranet'),
            value: '4',
          },
          {
            text: this.$i18n.t('message.owasp_rr_threat_size_partners'),
            value: '5',
          },
          {
            text: this.$i18n.t('message.owasp_rr_threat_size_auth_users'),
            value: '6',
          },
          {
            text: this.$i18n.t(
              'message.owasp_rr_threat_size_anonymous_internet_users',
            ),
            value: '9',
          },
        ],
        lc: [
          {
            text: this.$i18n.t(
              'message.owasp_rr_loss_of_confidentiality_minimal_non_sensitive',
            ),
            value: '2',
          },
          {
            text:
              this.$i18n.t(
                'message.owasp_rr_loss_of_confidentiality_minimal_critical',
              ) +
              '/' +
              this.$i18n.t(
                'message.owasp_rr_loss_of_confidentiality_extensive_non_sensitive',
              ),
            value: '6',
          },
          {
            text: this.$i18n.t(
              'message.owasp_rr_loss_of_confidentiality_extensive_critical',
            ),
            value: '7',
          },
          {
            text: this.$i18n.t('message.owasp_rr_loss_of_confidentiality_all'),
            value: '9',
          },
        ],
        li: [
          {
            text: this.$i18n.t(
              'message.owasp_rr_loss_of_integrity_minimal_slightly_corrupt',
            ),
            value: '1',
          },
          {
            text: this.$i18n.t(
              'message.owasp_rr_loss_of_integrity_minimal_seriously_corrupt',
            ),
            value: '3',
          },
          {
            text: this.$i18n.t(
              'message.owasp_rr_loss_of_integrity_extensive_slightly_corrupt',
            ),
            value: '5',
          },
          {
            text: this.$i18n.t(
              'message.owasp_rr_loss_of_integrity_extensive_seriously_corrupt',
            ),
            value: '7',
          },
          {
            text: this.$i18n.t('message.owasp_rr_loss_of_integrity_all'),
            value: '9',
          },
        ],
        lav: [
          {
            text: this.$i18n.t(
              'message.owasp_rr_loss_of_availability_minimal_secondary_services',
            ),
            value: '1',
          },
          {
            text:
              this.$i18n.t(
                'message.owasp_rr_loss_of_availability_minimal_primary_services',
              ) +
              '/' +
              this.$i18n.t(
                'message.owasp_rr_loss_of_availability_extensive_secondary_services',
              ),
            value: '5',
          },
          {
            text: this.$i18n.t(
              'message.owasp_rr_loss_of_availability_extensive_primary_services',
            ),
            value: '7',
          },
          {
            text: this.$i18n.t('message.owasp_rr_loss_of_availability_all'),
            value: '9',
          },
        ],
        lac: [
          {
            text: this.$i18n.t(
              'message.owasp_rr_loss_of_accountability_fully_traceable',
            ),
            value: '1',
          },
          {
            text: this.$i18n.t(
              'message.owasp_rr_loss_of_accountability_possibly_traceable',
            ),
            value: '7',
          },
          {
            text: this.$i18n.t(
              'message.owasp_rr_loss_of_accountability_completely_anonymous',
            ),
            value: '9',
          },
        ],
        ed: [
          {
            text: this.$i18n.t('message.owasp_rr_ease_of_discovery_impossible'),
            value: '1',
          },
          {
            text: this.$i18n.t('message.owasp_rr_ease_of_discovery_difficult'),
            value: '3',
          },
          {
            text: this.$i18n.t('message.owasp_rr_ease_of_discovery_easy'),
            value: '7',
          },
          {
            text: this.$i18n.t(
              'message.owasp_rr_ease_of_discovery_automated_tools_available',
            ),
            value: '9',
          },
        ],
        ee: [
          {
            text: this.$i18n.t('message.owasp_rr_ease_of_exploit_theoritical'),
            value: '1',
          },
          {
            text: this.$i18n.t('message.owasp_rr_ease_of_exploit_difficult'),
            value: '3',
          },
          {
            text: this.$i18n.t('message.owasp_rr_ease_of_exploit_easy'),
            value: '5',
          },
          {
            text: this.$i18n.t(
              'message.owasp_rr_ease_of_exploit_automated_tools_available',
            ),
            value: '9',
          },
        ],
        a: [
          {
            text: this.$i18n.t('message.owasp_rr_awareness_unknown'),
            value: '1',
          },
          {
            text: this.$i18n.t('message.owasp_rr_awareness_hidden'),
            value: '4',
          },
          {
            text: this.$i18n.t('message.owasp_rr_awareness_obvious'),
            value: '6',
          },
          {
            text: this.$i18n.t('message.owasp_rr_awareness_public_knowledge'),
            value: '9',
          },
        ],
        id: [
          {
            text: this.$i18n.t('message.owasp_rr_intrusion_detection_active'),
            value: '1',
          },
          {
            text: this.$i18n.t(
              'message.owasp_rr_intrusion_detection_logged_reviewed',
            ),
            value: '3',
          },
          {
            text: this.$i18n.t(
              'message.owasp_rr_intrusion_detection_logged_unreviewed',
            ),
            value: '8',
          },
          {
            text: this.$i18n.t(
              'message.owasp_rr_intrusion_detection_not_logged',
            ),
            value: '9',
          },
        ],
        fd: [
          {
            text: this.$i18n.t(
              'message.owasp_rr_financial_damage_less_than_cost_to_fix',
            ),
            value: '1',
          },
          {
            text: this.$i18n.t('message.owasp_rr_financial_damage_minor'),
            value: '3',
          },
          {
            text: this.$i18n.t('message.owasp_rr_financial_damage_significant'),
            value: '7',
          },
          {
            text: this.$i18n.t('message.owasp_rr_financial_damage_bankruptcy'),
            value: '9',
          },
        ],
        rd: [
          {
            text: this.$i18n.t('message.owasp_rr_reputation_damage_minimal'),
            value: '1',
          },
          {
            text: this.$i18n.t(
              'message.owasp_rr_reputation_damage_major_accounts',
            ),
            value: '4',
          },
          {
            text: this.$i18n.t('message.owasp_rr_reputation_damage_goodwill'),
            value: '5',
          },
          {
            text: this.$i18n.t('message.owasp_rr_reputation_damage_brand'),
            value: '9',
          },
        ],
        nc: [
          {
            text: this.$i18n.t('message.owasp_rr_non_compliance_minor'),
            value: '2',
          },
          {
            text: this.$i18n.t('message.owasp_rr_non_compliance_clear'),
            value: '5',
          },
          {
            text: this.$i18n.t('message.owasp_rr_non_compliance_high_profile'),
            value: '7',
          },
        ],
        pv: [
          {
            text: this.$i18n.t(
              'message.owasp_rr_privacy_violation_one_individual',
            ),
            value: '3',
          },
          {
            text: this.$i18n.t('message.owasp_rr_privacy_violation_hundreds'),
            value: '5',
          },
          {
            text: this.$i18n.t('message.owasp_rr_privacy_violation_thousands'),
            value: '7',
          },
          {
            text: this.$i18n.t('message.owasp_rr_privacy_violation_millions'),
            value: '9',
          },
        ],
      },
    };
  },
  watch: {
    vulnProp(newVuln, oldVuln) {
      if (
        !this.vulnerability.uuid ||
        (oldVuln.uuid && newVuln.uuid && oldVuln.uuid !== newVuln.uuid)
      ) {
        this.cvssv2Vector = {
          av: null,
          ac: null,
          au: null,
          c: null,
          i: null,
          a: null,
        };
        this.cvssv3Vector = {
          av: null,
          ac: null,
          pr: null,
          ui: null,
          s: null,
          c: null,
          i: null,
          a: null,
        };
        this.owaspRRVector = {
          sl: null,
          m: null,
          o: null,
          s: null,
          ed: null,
          ee: null,
          a: null,
          id: null,
          lc: null,
          li: null,
          lav: null,
          lac: null,
          fd: null,
          rd: null,
          nc: null,
          pv: null,
        };
        this.cvssV2Score = {
          baseScore: 0,
          impactSubScore: 0,
          exploitabilitySubScore: 0,
        };
        this.cvssV3Score = {
          baseScore: 0,
          impactSubScore: 0,
          exploitabilitySubScore: 0,
        };
        this.owaspRRScore = {
          likelihoodScore: 0,
          technicalImpactScore: 0,
          businessImpactScore: 0,
        };
        this.vulnerability = newVuln;
        this.onShow();
        if (this.vulnerability.cwes && this.vulnerability.cwes.length > 0) {
          this.selectableCwes = this.vulnerability.cwes;
        }
      }
    },
  },
  computed: {
    columns() {
      let columns = [
        {
          title: this.$t('message.component'),
          field: 'versionRange',
          sortable: true,
        },
        {
          title: this.$t('message.reported_by'),
          field: 'reportedBy',
          sortable: true,
          formatter(source) {
            return common.formatSourceLabel(source);
          },
        },
        {
          title: this.$t('message.first_seen'),
          field: 'firstSeenAt',
          sortable: true,
          visible: false, // Hide per default to not overload the table
          formatter(timestamp) {
            return typeof timestamp === 'number'
              ? common.formatTimestamp(timestamp, true)
              : '-';
          },
        },
        {
          title: this.$t('message.last_seen'),
          field: 'lastSeenAt',
          sortable: true,
          formatter(timestamp) {
            return typeof timestamp === 'number'
              ? common.formatTimestamp(timestamp, true)
              : '-';
          },
        },
      ];
      this.canChange =
        this.vulnerability.source === 'INTERNAL' &&
        this.isPermitted(this.PERMISSIONS.VULNERABILITY_MANAGEMENT);
      if (this.canChange) {
        columns.push({
          title: 'Actions',
          field: 'actions',
          visible: this.vulnerability.source === 'INTERNAL',
          footerFormatter: this.actionFooter,
          formatter: (value, row, index) => {
            return `
              <button class="btn btn-primary edit" data-id="${index}">${this.$t('message.edit')}</button>
              <button variant="outline-danger" class="btn btn-danger delete" data-id="${value}">${this.$t('message.delete')}</button>
            `;
          },
          events: {
            'click .edit': this.handleEdit,
            'click .delete': this.handleDelete,
          },
        });
      }
      return columns;
    },
    options() {
      return {
        search: true,
        showColumns: true,
        showRefresh: false,
        showFooter: this.canChange,
        pagination: true,
        silentSort: false,
        sidePagination: 'client',
        pageList: '[10, 25]',
        pageSize: 10,
        toolbar: '#vulnerableSoftwareToolbar',
        onPostBody: this.attachFooterEvent,
      };
    },
    computedCvssSeverity() {
      let severity = 'UNASSIGNED';
      if (this.cvssV2Score && this.cvssV2Score.baseScore) {
        if (this.cvssV2Score.baseScore >= 7) {
          severity = 'HIGH';
        } else if (this.cvssV2Score.baseScore >= 4) {
          severity = 'MEDIUM';
        } else if (this.cvssV2Score.baseScore > 0) {
          severity = 'LOW';
        }
      }
      if (this.cvssV3Score && this.cvssV3Score.baseScore) {
        if (this.cvssV3Score.baseScore >= 9) {
          severity = 'CRITICAL';
        } else if (this.cvssV3Score.baseScore >= 7) {
          severity = 'HIGH';
        } else if (this.cvssV3Score.baseScore >= 4) {
          severity = 'MEDIUM';
        } else if (this.cvssV3Score.baseScore > 0) {
          severity = 'LOW';
        }
      }
      return severity;
    },
    computedOwaspRRSeverity() {
      let severity = 'UNASSIGNED';
      if (
        this.owaspRRScore.likelihoodScore &&
        this.owaspRRScore.businessImpactScore &&
        this.owaspRRScore.technicalImpactScore
      ) {
        let likelihoodSeverity = this.getOwaspRRSeverity(
          this.owaspRRScore.likelihoodScore,
        );
        let impactSeverity = this.getOwaspRRSeverity(
          Math.max(
            this.owaspRRScore.businessImpactScore,
            this.owaspRRScore.technicalImpactScore,
          ),
        );
        severity =
          common.OWASP_RR_LIKELIHOOD_TO_IMPACT_SEVERITY_MATRIX[
            likelihoodSeverity
          ][impactSeverity] || 'UNASSIGNED';
      }
      return severity;
    },
    isReadonly() {
      return (
        this.isNotPermitted(this.PERMISSIONS.VULNERABILITY_MANAGEMENT) ||
        this.vulnerability.source !== 'INTERNAL'
      );
    },
  },
  methods: {
    actionFooter() {
      return `
        <button
          id="footer-button"
          class="btn btn-primary action-icon"
        >
          <span class="fa fa-plus-square"></span>
        </button>
      `;
    },
    attachFooterEvent() {
      if (this.columns != null) {
        try {
          document
            .getElementById('footer-button')
            .addEventListener('click', this.openAddAffectedComponentModal);
        } catch {}
      }
    },
    openAddAffectedComponentModal() {
      this.$root.$emit('bv::show::modal', 'addAffectedComponentModal');
    },
    resolveVulnAliases: function (aliases) {
      return common.resolveVulnAliases(this.vulnerability.source, aliases);
    },
    onShow: function () {
      this.parseCvssV2Vector();
      this.parseCvssV3Vector();
      this.parseOwaspRRVector();
      this.getAttributions();
    },
    updateVulnerability: function () {
      let url = `${this.$api.BASE_URL}/${this.$api.URL_VULNERABILITY}`;
      this.vulnerability.affectedComponents.forEach((affectedComponent) => {
        affectedComponent.uuid <= this.newAdded
          ? (affectedComponent.uuid = null)
          : null;
      });
      this.axios
        .post(url, {
          uuid: this.vulnerability.uuid,
          vulnId: this.vulnerability.vulnId,
          source: 'INTERNAL',
          title: this.vulnerability.title,
          description: this.vulnerability.description,
          detail: this.vulnerability.detail,
          recommendation: this.vulnerability.recommendation,
          references: this.vulnerability.references,
          created: this.vulnerability.created,
          published: this.vulnerability.published,
          updated: this.vulnerability.updated,
          severity: this.vulnerability.severity,
          cvssV2Vector: this.generateCvssV2Vector(),
          cvssV3Vector: this.generateCvssV3Vector(),
          owaspRRVector: this.generateOwaspRRVector(),
          cwes: this.selectableCwes,
          affectedComponents: this.vulnerability.affectedComponents,
        })
        .then((response) => {
          this.$emit('vulnerabilityUpdated', response.data);
          this.$toastr.s(this.$t('message.vulnerability_updated'));
        })
        .catch((error) => {
          this.$toastr.w(this.$t('condition.unsuccessful_action'));
        })
        .finally(() => {
          this.$root.$emit('bv::hide::modal', 'vulnerabilityDetailsModal');
        });
    },
    deleteVulnerability: function () {
      this.$root.$emit('bv::hide::modal', 'vulnerabilityDetailsModal');
      let url =
        `${this.$api.BASE_URL}/${this.$api.URL_VULNERABILITY}/` +
        this.vulnerability.uuid;
      this.axios
        .delete(url)
        .then((response) => {
          this.$toastr.s(this.$t('message.vulnerability_deleted'));
          this.$router.replace({ path: '/vulnerabilities' });
        })
        .catch((error) => {
          this.$toastr.w(this.$t('condition.unsuccessful_action'));
        });
    },
    updateCweSelection: function (selections) {
      this.$root.$emit('bv::hide::modal', 'selectCweModal');
      for (let i = 0; i < selections.length; i++) {
        let selection = selections[i];
        this.selectableCwes.push({
          cweId: selection.cweId,
          name: selection.name,
        });
      }
    },
    removeCwe: function (cwe) {
      for (let i = 0; i < this.selectableCwes.length; i++) {
        let selectedCwe = this.selectableCwes[i];
        if (selectedCwe.cweId === cwe.cweId) {
          this.selectableCwes.splice(i, 1);
          i--;
        }
      }
    },
    removeAffectedComponent: function (affectedComponent) {
      for (let i = 0; i < this.vulnerability.affectedComponents.length; i++) {
        let a = this.vulnerability.affectedComponents[i];
        if (isEqual(a, affectedComponent)) {
          this.vulnerability.affectedComponents.splice(i, 1);
        }
      }
    },
    parseCvssV2Vector: function () {
      const vector = {};
      if (this.vulnerability && this.vulnerability.cvssV2Vector) {
        this.vulnerability.cvssV2Vector.split('/').forEach((v) => {
          const temp = v.split(':');
          vector[temp[0].replace(/[^a-z]/gi, '')] = temp[1].replace(
            /[^a-z]/gi,
            '',
          );
        });
        this.cvssv2Vector.ac = vector.AC;
        this.cvssv2Vector.av = vector.AV;
        this.cvssv2Vector.au = vector.Au;
        this.cvssv2Vector.c = vector.C;
        this.cvssv2Vector.i = vector.I;
        this.cvssv2Vector.a = vector.A;
        if (
          this.vulnerability.cvssV2BaseScore &&
          this.vulnerability.cvssV2ImpactSubScore &&
          this.vulnerability.cvssV2ExploitabilitySubScore
        ) {
          this.cvssV2Score.baseScore = this.vulnerability.cvssV2BaseScore;
          this.cvssV2Score.impactSubScore =
            this.vulnerability.cvssV2ImpactSubScore;
          this.cvssV2Score.exploitabilitySubScore =
            this.vulnerability.cvssV2ExploitabilitySubScore;
        } else {
          this.retrieveCvssScore(this.vulnerability.cvssV2Vector, 2);
        }
      }
      return vector;
    },
    parseCvssV3Vector: function () {
      const vector = {};
      if (this.vulnerability && this.vulnerability.cvssV3Vector) {
        this.vulnerability.cvssV3Vector.split('/').forEach((v) => {
          const temp = v.split(':');
          vector[temp[0].replace(/[^a-z]/gi, '')] = temp[1].replace(
            /[^a-z]/gi,
            '',
          );
        });
        this.cvssv3Vector.ac = vector.AC;
        this.cvssv3Vector.av = vector.AV;
        this.cvssv3Vector.pr = vector.PR;
        this.cvssv3Vector.ui = vector.UI;
        this.cvssv3Vector.s = vector.S;
        this.cvssv3Vector.c = vector.C;
        this.cvssv3Vector.i = vector.I;
        this.cvssv3Vector.a = vector.A;
        if (
          this.vulnerability.cvssV3BaseScore &&
          this.vulnerability.cvssV3ImpactSubScore &&
          this.vulnerability.cvssV3ExploitabilitySubScore
        ) {
          this.cvssV3Score.baseScore = this.vulnerability.cvssV3BaseScore;
          this.cvssV3Score.impactSubScore =
            this.vulnerability.cvssV3ImpactSubScore;
          this.cvssV3Score.exploitabilitySubScore =
            this.vulnerability.cvssV3ExploitabilitySubScore;
        } else {
          this.retrieveCvssScore(this.vulnerability.cvssV3Vector, 3);
        }
      }
      return vector;
    },
    parseOwaspRRVector: function () {
      const vector = {};
      if (this.vulnerability && this.vulnerability.owaspRRVector) {
        this.vulnerability.owaspRRVector.split('/').forEach((v) => {
          const temp = v.split(':');
          vector[temp[0].replace(/[^a-z]/gi, '')] = temp[1].replace(
            /[^0-9]/gi,
            '',
          );
        });
        this.owaspRRVector.sl = vector.SL;
        this.owaspRRVector.m = vector.M;
        this.owaspRRVector.o = vector.O;
        this.owaspRRVector.s = vector.S;
        this.owaspRRVector.ed = vector.ED;
        this.owaspRRVector.ee = vector.EE;
        this.owaspRRVector.a = vector.A;
        this.owaspRRVector.id = vector.ID;
        this.owaspRRVector.lc = vector.LC;
        this.owaspRRVector.li = vector.LI;
        this.owaspRRVector.lav = vector.LAV;
        this.owaspRRVector.lac = vector.LAC;
        this.owaspRRVector.fd = vector.FD;
        this.owaspRRVector.rd = vector.RD;
        this.owaspRRVector.nc = vector.NC;
        this.owaspRRVector.pv = vector.PV;
        if (
          this.vulnerability.owaspRRLikelihoodScore &&
          this.vulnerability.owaspRRTechnicalImpactScore &&
          this.vulnerability.owaspRRBusinessImpactScore
        ) {
          this.owaspRRScore.likelihoodScore =
            this.vulnerability.owaspRRLikelihoodScore;
          this.owaspRRScore.technicalImpactScore =
            this.vulnerability.owaspRRTechnicalImpactScore;
          this.owaspRRScore.businessImpactScore =
            this.vulnerability.owaspRRBusinessImpactScore;
        } else {
          this.retrieveOwaspRRScore(this.vulnerability.owaspRRVector);
        }
      }
      return vector;
    },
    generateCvssV2Vector: function () {
      if (
        this.cvssv2Vector.av != null &&
        this.cvssv2Vector.ac != null &&
        this.cvssv2Vector.au != null &&
        this.cvssv2Vector.c != null &&
        this.cvssv2Vector.i != null &&
        this.cvssv2Vector.a != null
      ) {
        let vector =
          '(AV:' +
          this.cvssv2Vector.av +
          '/AC:' +
          this.cvssv2Vector.ac +
          '/Au:' +
          this.cvssv2Vector.au +
          '/C:' +
          this.cvssv2Vector.c +
          '/I:' +
          this.cvssv2Vector.i +
          '/A:' +
          this.cvssv2Vector.a +
          ')';
        this.retrieveCvssScore(vector, 2);
        return vector;
      }
      return null;
    },
    generateCvssV3Vector: function () {
      if (
        this.cvssv3Vector.av != null &&
        this.cvssv3Vector.ac != null &&
        this.cvssv3Vector.pr != null &&
        this.cvssv3Vector.ui != null &&
        this.cvssv3Vector.s != null &&
        this.cvssv3Vector.c != null &&
        this.cvssv3Vector.i != null &&
        this.cvssv3Vector.a != null
      ) {
        let vector =
          '(AV:' +
          this.cvssv3Vector.av +
          '/AC:' +
          this.cvssv3Vector.ac +
          '/PR:' +
          this.cvssv3Vector.pr +
          '/UI:' +
          this.cvssv3Vector.ui +
          '/S:' +
          this.cvssv3Vector.s +
          '/C:' +
          this.cvssv3Vector.c +
          '/I:' +
          this.cvssv3Vector.i +
          '/A:' +
          this.cvssv3Vector.a +
          ')';
        this.retrieveCvssScore(vector, 3);
        return vector;
      }
      return null;
    },
    generateOwaspRRVector: function () {
      if (
        this.owaspRRVector.sl != null &&
        this.owaspRRVector.m != null &&
        this.owaspRRVector.o != null &&
        this.owaspRRVector.s != null &&
        this.owaspRRVector.ed != null &&
        this.owaspRRVector.ee != null &&
        this.owaspRRVector.a != null &&
        this.owaspRRVector.id != null &&
        this.owaspRRVector.lc != null &&
        this.owaspRRVector.li != null &&
        this.owaspRRVector.lav != null &&
        this.owaspRRVector.lac != null &&
        this.owaspRRVector.fd != null &&
        this.owaspRRVector.rd != null &&
        this.owaspRRVector.nc != null &&
        this.owaspRRVector.pv != null
      ) {
        let vector = `SL:${this.owaspRRVector.sl}/M:${this.owaspRRVector.m}/O:${this.owaspRRVector.o}/S:${this.owaspRRVector.s}/ED:${this.owaspRRVector.ed}/EE:${this.owaspRRVector.ee}/A:${this.owaspRRVector.a}/ID:${this.owaspRRVector.id}/LC:${this.owaspRRVector.lc}/LI:${this.owaspRRVector.li}/LAV:${this.owaspRRVector.lav}/LAC:${this.owaspRRVector.lac}/FD:${this.owaspRRVector.fd}/RD:${this.owaspRRVector.rd}/NC:${this.owaspRRVector.nc}/PV:${this.owaspRRVector.pv}`;
        this.retrieveOwaspRRScore(vector);
        return vector;
      }
      return null;
    },
    retrieveCvssScore: function (vector, cvssVersion) {
      let url = `${this.$api.BASE_URL}/${this.$api.URL_CALCULATOR_CVSS}`;
      this.axios
        .get(url, { params: { vector: vector } })
        .then((response) => {
          if (cvssVersion === 2) {
            this.cvssV2Score.baseScore = response.data.baseScore;
            this.cvssV2Score.impactSubScore = response.data.impactSubScore;
            this.cvssV2Score.exploitabilitySubScore =
              response.data.exploitabilitySubScore;
          } else if (cvssVersion === 3) {
            this.cvssV3Score.baseScore = response.data.baseScore;
            this.cvssV3Score.impactSubScore = response.data.impactSubScore;
            this.cvssV3Score.exploitabilitySubScore =
              response.data.exploitabilitySubScore;
          }
        })
        .catch((error) => {
          this.$toastr.w(this.$t('condition.unsuccessful_action'));
        });
    },
    retrieveOwaspRRScore: function (vector) {
      let url = `${this.$api.BASE_URL}/${this.$api.URL_CALCULATOR_OWASP}`;
      this.axios
        .get(url, { params: { vector: vector } })
        .then((response) => {
          this.owaspRRScore.likelihoodScore = response.data.likelihoodScore;
          this.owaspRRScore.technicalImpactScore =
            response.data.technicalImpactScore;
          this.owaspRRScore.businessImpactScore =
            response.data.businessImpactScore;
        })
        .catch((error) => {
          this.$toastr.w(this.$t('condition.unsuccessful_action'));
        });
    },
    severityColor(score) {
      if (!score) {
        return this.primaryColor;
      } else if (score >= 9.0) {
        return this.criticalColor;
      } else if (score >= 7.0) {
        return this.highColor;
      } else if (score >= 4.0) {
        return this.mediumColor;
      } else {
        return this.lowColor;
      }
    },
    owaspRRSeverityColor(score) {
      if (!score) {
        return this.primaryColor;
      } else if (score >= 9.0) {
        return this.criticalColor;
      } else if (score >= 6.0) {
        return this.highColor;
      } else if (score >= 3.0) {
        return this.mediumColor;
      } else {
        return this.lowColor;
      }
    },
    getOwaspRRSeverity(score) {
      let severity = 'UNASSIGNED';
      if (score >= 6) {
        severity = 'HIGH';
      } else if (score >= 3) {
        severity = 'MEDIUM';
      } else if (score > 0) {
        severity = 'LOW';
      }
      return severity;
    },
    addAffectedComponent: function (affectedComponent) {
      if (!this.vulnerability.affectedComponents) {
        this.vulnerability.affectedComponents = [];
      }
      let affectedComponentIndex =
        this.vulnerability.affectedComponents.findIndex(
          (obj) => obj.uuid === affectedComponent.uuid,
        );
      if (affectedComponentIndex >= 0) {
        this.vulnerability.affectedComponents[affectedComponentIndex] =
          affectedComponent;
        this.getAttributions();
      } else {
        affectedComponent['affectedVersionAttributions'] = [{}];
        this.affectedVersionAttributions.push({
          uuid: this.newAdded,
          reportedBy: 'INTERNAL',
          firstSeenAt: '/',
          lastSeenAt: '/',
          versionRange: this.affectedComponentLabel(affectedComponent),
        });
        affectedComponent['uuid'] = this.newAdded++;
        this.vulnerability.affectedComponents.push(affectedComponent);
      }
    },
    handleDelete(event, value, row, index) {
      let affectedComponentIndex =
        this.vulnerability.affectedComponents.findIndex(
          (obj) => obj.uuid === row.uuid,
        );
      if (affectedComponentIndex < 0) {
        return;
      }
      let affectedComponent =
        this.vulnerability.affectedComponents[affectedComponentIndex];
      let attributionIndex =
        affectedComponent.affectedVersionAttributions.findIndex(
          (obj) => obj.uuid === row.attrUuid,
        );
      if (attributionIndex < 0) {
        return;
      }
      affectedComponent.affectedVersionAttributions.splice(attributionIndex, 1);
      if (affectedComponent.affectedVersionAttributions.length == 0) {
        this.vulnerability.affectedComponents.splice(affectedComponentIndex, 1);
      }
      this.affectedVersionAttributions.splice(index, 1);
    },
    handleEdit(event, value, row, index) {
      let affectedComponent = this.vulnerability.affectedComponents.find(
        (obj) => obj.uuid === row.uuid,
      );
      if (!affectedComponent) {
        return;
      }
      this.$root.$emit('object-event', affectedComponent);
      this.$root.$emit('bv::show::modal', 'addAffectedComponentModal');
    },
    resetValues: function () {
      this.cvssV2Score = {
        baseScore: 0,
        impactSubScore: 0,
        exploitabilitySubScore: 0,
      };
      this.cvssV3Score = {
        baseScore: 0,
        impactSubScore: 0,
        exploitabilitySubScore: 0,
      };
      this.owaspRRScore = {
        likelihoodScore: 0,
        technicalImpactScore: 0,
        businessImpactScore: 0,
      };
      this.cvssv2Vector = {
        av: null,
        ac: null,
        au: null,
        c: null,
        i: null,
        a: null,
      };
      this.cvssv3Vector = {
        av: null,
        ac: null,
        pr: null,
        ui: null,
        s: null,
        c: null,
        i: null,
        a: null,
      };
      this.owaspRRVector = {
        sl: null,
        m: null,
        o: null,
        s: null,
        ed: null,
        ee: null,
        a: null,
        id: null,
        lc: null,
        li: null,
        lav: null,
        lac: null,
        fd: null,
        rd: null,
        nc: null,
        pv: null,
      };
      this.vulnerability = {
        vulnId: null, // This has to be null on initial load so that the vulnId gets populated.
      };
    },
    getAttributions: function () {
      this.affectedVersionAttributions = [];
      let components = this.vulnerability.affectedComponents;
      for (let i = 0; i < components.length; i++) {
        let affectedComponent = components[i];
        for (
          let j = 0;
          j < affectedComponent.affectedVersionAttributions.length;
          j++
        ) {
          let attribution = affectedComponent.affectedVersionAttributions[j];
          this.affectedVersionAttributions.push({
            uuid: affectedComponent.uuid,
            attrUuid: attribution.uuid,
            versionRange: this.affectedComponentLabel(affectedComponent),
            reportedBy: attribution.source,
            firstSeenAt: attribution.firstSeen,
            lastSeenAt: attribution.lastSeen,
          });
        }
      }
    },
    affectedComponentLabel: function (affectedComponent) {
      let s = affectedComponent.identity;
      if (affectedComponent.versionType === 'RANGE') {
        let rangeParts = [];
        if (affectedComponent.versionStartExcluding) {
          rangeParts.push(`>${affectedComponent.versionStartExcluding}`);
        } else if (affectedComponent.versionStartIncluding) {
          rangeParts.push(`>=${affectedComponent.versionStartIncluding}`);
        }
        if (affectedComponent.versionEndExcluding) {
          rangeParts.push(`<${affectedComponent.versionEndExcluding}`);
        } else if (affectedComponent.versionEndIncluding) {
          rangeParts.push(`<=${affectedComponent.versionEndIncluding}`);
        }
        if (rangeParts.length > 0) {
          s += ` (${rangeParts.join('|')})`;
        }
      } else if (
        affectedComponent.identityType === 'PURL' &&
        affectedComponent.versionType === 'EXACT' &&
        !s.includes('@')
      ) {
        s += '@' + affectedComponent.version;
      }
      return s;
    },
  },
};
</script>

<style lang="scss">
@import '../../../assets/scss/vendors/vue-tags-input/vue-tags-input';
</style>

<style scoped>
.tab-content .tab-pane {
  padding: 0 !important;
}
.tab-content {
  border: 0 !important;
}
.card {
  border: 0;
  padding: 0;
  margin-bottom: 0;
}
</style>
<style>
.cvss-calc {
  width: 100%;
}
.cvss-calc-2-btn > .btn {
  width: 50%;
  max-width: 50%;
}
.cvss-calc-3-btn > .btn {
  width: 33%;
  max-width: 33%;
}
.cvss-calc-4-btn > .btn {
  width: 25%;
  max-width: 25%;
}
.vue-easy-pie-chart .inner-text {
  color: var(--primary-lighter);
  font-size: 2rem;
  font-weight: bold;
}
</style>
